import React, { useRef } from 'react';

import { showToast } from '../../../common/Notification';

type Kind = 'client' | 'collab' | 'relance';

function EmailTemplatesPreview({ kind }: { kind: Kind }) {
  const contentRef = useRef<HTMLDivElement | null>(null);

  const copyHtmlFromRef = async(ref: React.RefObject<HTMLDivElement>): Promise<void> => {
    const container = ref.current;
    if (!container) { return; }
    const html = container.innerHTML;
    const text = container.innerText;
    try {
      const ClipboardItemCtor = typeof window !== 'undefined' ? window.ClipboardItem : undefined;
      const clipboard = navigator.clipboard;
      const canWriteRich = ClipboardItemCtor !== undefined && clipboard?.write;
      if (canWriteRich && ClipboardItemCtor && clipboard) {
        const data = [
          new ClipboardItemCtor({ 'text/html': new Blob([html], { type: 'text/html' }) }),
          new ClipboardItemCtor({ 'text/plain': new Blob([text], { type: 'text/plain' }) }),
        ];
        await clipboard.write(data);
        showToast('success', 'Contenu copié dans le presse-papiers.');
        return;
      }
      await navigator.clipboard.writeText(text);
      showToast('success', 'Contenu copié dans le presse-papiers.');
    } catch {
      try {
        const tmp = document.createElement('textarea');
        tmp.value = text;
        document.body.appendChild(tmp);
        tmp.select();
        document.execCommand('copy');
        document.body.removeChild(tmp);
        showToast('success', 'Contenu copié dans le presse-papiers.');
      } catch {}
    }
  };
  return (
    <div className="rounded-xl border-2 border-slate-200 bg-white p-5 shadow-sm">
      {kind === 'client' && (
        <div>
          <div className="relative">
            <button
              type="button"
              title="Copier"
              aria-label="Copier le contenu client"
              onClick={() => copyHtmlFromRef(contentRef)}
              className="absolute top-2 right-2 inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-2 py-1 text-xs shadow-sm hover:bg-slate-50"
            >
              <span className="inline-block h-4 w-4" aria-hidden="true" role="img" aria-label="clipboard">📋</span>
              Copier
            </button>
            <div>
              <div className="-mx-5 -mt-5 rounded-t-xl bg-rgpd-blue px-5 py-3 text-center font-bold text-white"><span role="img" aria-label="email">📧</span> APERÇU DE L'EMAIL CLIENT</div>
              <div className="mt-4 space-y-3 text-sm">
                <div className="grid grid-cols-[80px,1fr] gap-2">
                  <strong>De :</strong> <span className="rounded bg-blue-100 px-2">[AGENCE_NOM] &lt;contact@agence-immobilier.fr&gt;</span>
                </div>
                <div className="grid grid-cols-[80px,1fr] gap-2">
                  <strong>À :</strong> <span className="rounded bg-amber-100 px-2">[CLIENT_EMAIL]</span>
                </div>
                <div className="grid grid-cols-[80px,1fr] gap-2">
                  <strong>Objet :</strong> <span>Confirmation de votre rendez-vous d'estimation immobilière</span>
                </div>
                <div ref={contentRef} className="rounded border-l-4 border-rgpd-blue bg-blue-50 p-4">
                  <p>Bonjour <span className="rounded bg-amber-100 px-1 font-semibold">[CIVILITE] [NOM_CLIENT]</span>,</p>
                  <p className="mt-2">Suite à notre entretien téléphonique, nous vous confirmons votre rendez-vous d'estimation immobilière :</p>
                  <div className="mt-3 space-y-1">
                    <div><strong><span role="img" aria-label="calendar">📅</span> Date :</strong> <span className="rounded bg-amber-100 px-1 font-semibold">[DATE_RDV]</span></div>
                    <div><strong>⏰ Heure :</strong> <span className="rounded bg-amber-100 px-1 font-semibold">[HEURE_RDV]</span></div>
                    <div><strong><span role="img" aria-label="emoji">📍</span> Adresse :</strong> <span className="rounded bg-amber-100 px-1 font-semibold">[ADRESSE_BIEN]</span></div>
                    <div><strong><span role="img" aria-label="office building">🏢</span> Bien :</strong> <span className="rounded bg-amber-100 px-1 font-semibold">[TYPE_BIEN]</span></div>
                    <div><strong><span role="img" aria-label="person">👤</span> Votre expert :</strong> <span className="rounded bg-emerald-100 px-1 font-semibold">[NOM_EXPERT]</span></div>
                  </div>
                  <p className="mt-4">Cordialement,<br/>L'équipe <span className="rounded bg-amber-100 px-1">[AGENCE_NOM]</span></p>
                </div>
              </div>
            </div>
          </div>
          <div className="mt-4 rounded-lg border border-blue-200 bg-blue-50 p-3">
            <div className="font-semibold text-blue-900"><span role="img" aria-label="wrench">🔧</span> Variables Automatiques</div>
            <p className="text-sm text-blue-900/80">Les éléments surlignés en jaune sont remplacés automatiquement par les données saisies lors de la prise de rendez-vous.</p>
          </div>
        </div>
      )}
      {kind === 'collab' && (
        <div>
          <div className="relative">
            <button
              type="button"
              title="Copier"
              aria-label="Copier le contenu collaborateur"
              onClick={() => copyHtmlFromRef(contentRef)}
              className="absolute top-2 right-2 inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-2 py-1 text-xs shadow-sm hover:bg-slate-50"
            >
              <span className="inline-block h-4 w-4" aria-hidden="true" role="img" aria-label="clipboard">📋</span>
              Copier
            </button>
            <div>
              <div className="-mx-5 -mt-5 rounded-t-xl bg-emerald-600 px-5 py-3 text-center font-bold text-white"><span role="img" aria-label="email">📧</span> APERÇU DE L'EMAIL COLLABORATEUR</div>
              <div ref={contentRef} className="mt-4 space-y-2 text-sm">
                <p>Bonjour <span className="rounded bg-emerald-100 px-1 font-semibold">[PRENOM_COLLABORATEUR]</span>,</p>
                <p>Un nouveau rendez-vous d'estimation immobilière vient de vous être assigné.</p>
                <div className="rounded border-l-4 border-emerald-600 bg-emerald-50 p-4">
                  <div className="mb-2 font-semibold text-emerald-900"><span role="img" aria-label="calendar">📅</span> INFORMATIONS DU RENDEZ-VOUS</div>
                  <div className="space-y-1">
                    <div><strong><span role="img" aria-label="calendar">📅</span> Date :</strong> <span className="rounded bg-amber-100 px-1 font-semibold">[DATE_RDV]</span></div>
                    <div><strong>⏰ Heure :</strong> <span className="rounded bg-amber-100 px-1 font-semibold">[HEURE_RDV]</span></div>
                    <div><strong><span role="img" aria-label="person">👤</span> Client :</strong> <span className="rounded bg-amber-100 px-1 font-semibold">[CIVILITE] [NOM_CLIENT]</span></div>
                    <div><strong><span role="img" aria-label="emoji">📍</span> Adresse :</strong> <span className="rounded bg-amber-100 px-1 font-semibold">[ADRESSE_BIEN]</span></div>
                  </div>
                </div>
                <p>Merci de confirmer la prise en compte dans votre interface transactionneur.</p>
                <p className="mt-4">Bonne journée !<br/>Le service référent</p>
              </div>
            </div>
          </div>
        </div>
      )}
      {kind === 'relance' && (
        <div>
          <div className="relative">
            <button
              type="button"
              title="Copier"
              aria-label="Copier le contenu de relance"
              onClick={() => copyHtmlFromRef(contentRef)}
              className="absolute top-2 right-2 inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-2 py-1 text-xs shadow-sm hover:bg-slate-50"
            >
              <span className="inline-block h-4 w-4" aria-hidden="true" role="img" aria-label="clipboard">📋</span>
              Copier
            </button>
            <div>
              <div className="-mx-5 -mt-5 rounded-t-xl bg-rgpd-blue px-5 py-3 text-center font-bold text-white"><span role="img" aria-label="email">📧</span> APERÇU DE L'EMAIL DE RELANCE</div>
              <div ref={contentRef} className="mt-4 space-y-2 text-sm">
                <p>Bonjour <span className="rounded bg-emerald-100 px-1 font-semibold">[PRENOM_COLLABORATEUR]</span>,</p>
                <div className="rounded border-l-4 border-rose-600 bg-rose-50 p-4">
                  <div className="mb-1 font-semibold text-rose-700"><span role="img" aria-label="emoji">⚠</span>️ RAPPEL IMPORTANT</div>
                  <p>Nous n'avons pas encore reçu la confirmation de prise en compte de votre rendez-vous.</p>
                </div>
                <p>Merci de confirmer rapidement ou de nous contacter en cas de problème.</p>
                <p className="mt-4">Cordialement,<br/>Le service référent</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default EmailTemplatesPreview;


